import {ChangeDetectionStrategy, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Reducers} from '@app/reducers';
import {ConfigService} from '@config/config.service';
import {Actions, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {ContactRequest} from '@services/associations/model/contactRequest';
import {ContactResponse} from '@services/associations/model/contactResponse';
import {PermissionsModel} from '@shared/models/permission.model';
import {SharedService} from '@shared/shared.service';
import {ValidateEmptyOrObject} from '@shared/validators/empty-or-object.validator';
import {BehaviorSubject, combineLatest, Observable, Subject, takeUntil} from 'rxjs';
import {filter, take} from 'rxjs/operators';
import * as mainActions from '@main/ngrx/actions/main.actions';
import * as contactsActions from '@crm/ngrx/actions/contacts.actions';
import * as selectFromLogin from '@login/ngrx/login.selectors';
import {WebOwnProfileModel} from "@services/back-office/model/webOwnProfileModel";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-contact-dialog',
    templateUrl: './contact-dialog.component.html',
    styleUrls: ['./contact-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ContactDialogComponent implements OnInit, OnDestroy {
    /** Loading */
    loading$ = new BehaviorSubject(false);
    /** Form */
    contactsForm: UntypedFormGroup;
    phonesForm: UntypedFormGroup;
    emailsForm: UntypedFormGroup;
    /** Check if Close Dialog after Save */
    closeDialogFlag: boolean;
    /** Current User */
    currentUser$: Observable<WebOwnProfileModel | null>;
    currentUser: WebOwnProfileModel | null;
    /** Permissions */
    permissions$: Observable<PermissionsModel[] | null>;
    permissions: PermissionsModel[] | null;
    readonlyFlag: boolean;
    archived: FormControl = new FormControl(false);
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();
    /** Scrollbar */
    @ViewChild('scrollableContent') private scrollableContent: ElementRef;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            edit: boolean,
            contactToEdit?: ContactResponse,
            phoneNumber?: string
        },
        public dialogRef: MatDialogRef<ContactDialogComponent>,
        private translateService: TranslateService,
        private formBuilder: UntypedFormBuilder,
        private config: ConfigService,
        private store$: Store<Reducers>,
        private actions$: Actions,
    ) {
        /** Select Current User */
        this.currentUser$ = this.store$.pipe(select(selectFromLogin.selectCurrentUser));
        /** Permissions */
        this.permissions$ = this.store$.pipe(select(selectFromLogin.selectPermissions));
    }

    ngOnInit(): void {
        /** Get Permissions From Observable */
        this.getPermissionsFromObservable();
        /** Get Current User From Observable */
        this.getCurrentUserFromObservable();

        combineLatest([this.permissions$, this.currentUser$]).pipe(
            filter(data => {
                return !!data[0] || !!data[1];
            }),
            take(1),
        ).subscribe(() => {
            if (!this.data.edit && this.currentUser?.id && this.permissions?.length) {
                this.patchManager();
            }
        });

        if (!this.contactsForm) {
            this.initForm();
        }
        if (!this.phonesForm) {
            this.initPhonesFormArray();
        }
        if (!this.emailsForm) {
            this.initEmailsFormArray();
        }

        if (this.data.phoneNumber) {
            this.patchPhonesForm([this.data.phoneNumber]);
        }
        /** Edit Contact */
        if (this.data.edit && this.data.contactToEdit) {
            this.patchForm(this.data.contactToEdit);
            this.updateWidth();
        }
        /** Watch Actions */
        this.watchAddOrEditContactSuccess();
        this.watchAddOrEditContactFail();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.loading$.complete();
    }

    updateWidth() {
        this.dialogRef.updateSize(this.config.rightSideDialogProps.width, this.config.rightSideDialogProps.height);
    }

    /** Get Permissions from Observable */
    getPermissionsFromObservable() {
        this.permissions$.pipe(
            filter(data => !!data),
            take(1)
        ).subscribe((permissions: PermissionsModel[] | null) => {
                this.permissions = permissions;
            }
        );
    }

    /** Get Current User From Observable */
    getCurrentUserFromObservable() {
        this.currentUser$.pipe(
            filter(data => !!data),
            take(1),
        ).subscribe((currentUser: WebOwnProfileModel | null) => {
            this.currentUser = currentUser;
        });
    }

    /** Check Permissions for DOM Elements */
    checkPermissions(permission: string): boolean {
        return this.permissions ?
            SharedService.checkPermission(permission, this.permissions[0].permissions, this.permissions[0].permissionsExists) :
            false;
    }

    checkPermissionAndOwner(permission: string, managerPermission: string, element: ContactResponse | undefined) {
        if (this.checkPermissions(permission)) {
            return false;
        }

        return !(this.checkPermissions(managerPermission) && element?.manager?.id === this.currentUser?.id);
    }

    initForm() {
        this.contactsForm = this.formBuilder.group({
            lastName: ['', [Validators.required]],
            firstName: ['', [Validators.required]],
            manager: ['', [ValidateEmptyOrObject]],
            parentalName: [''],
            address: [''],
            source: ['CRM', [Validators.required]],
            type: ['']
        });
    }

    initPhonesFormArray() {
        this.phonesForm = this.formBuilder.group({
            phonesFormControls: this.formBuilder.array([this.initSetPhonesControls()])
        });
    }

    initSetPhonesControls() {
        return this.formBuilder.group({
            phone: [{value: '', disabled: true}, [Validators.required]],
        });
    }

    getPhonesControls() {
        return (this.phonesForm.get('phonesFormControls') as UntypedFormArray).controls;
    }

    getPhonesControlsQuantity() {
        return (this.phonesForm.get('phonesFormControls') as UntypedFormArray).controls.length;
    }

    addPhonesArrayItem() {
        (this.phonesForm.get('phonesFormControls') as UntypedFormArray).push(this.initSetPhonesControls());
        setTimeout(() => {
            this.scrollToBottom();
        }, 50);
    }

    deletePhonesArrayItem(key: number) {
        if (this.getPhonesControlsQuantity() > 1) {
            (this.phonesForm.get('phonesFormControls') as UntypedFormArray).removeAt(key);
        }
    }

    initEmailsFormArray() {
        this.emailsForm = this.formBuilder.group({
            emailsFormControls: this.formBuilder.array([this.initSetEmailsControls()])
        });
    }

    initSetEmailsControls() {
        return this.formBuilder.group({
            email: ['', [Validators.pattern(this.config.regex.email)]],
        });
    }

    getEmailsControls() {
        return (this.emailsForm.get('emailsFormControls') as UntypedFormArray).controls;
    }

    getEmailsControlsQuantity() {
        return (this.emailsForm.get('emailsFormControls') as UntypedFormArray).controls.length;
    }

    addEmailsArrayItem() {
        (this.emailsForm.get('emailsFormControls') as UntypedFormArray).push(this.initSetEmailsControls());
        setTimeout(() => {
            this.scrollToBottom();
        }, 50);
    }

    deleteEmailsArrayItem(key: number) {
        if (this.getEmailsControlsQuantity() > 1) {
            (this.emailsForm.get('emailsFormControls') as UntypedFormArray).removeAt(key);
        } else if (this.getEmailsControlsQuantity() === 1) {
            this.emailsForm.reset();
        }
    }

    patchForm(contact: ContactResponse) {
        if (!this.contactsForm) {
            this.initForm();
        }

        this.contactsForm.patchValue({
            ...contact.manager && {manager: contact.manager},
            ...contact.lastName && {lastName: contact.lastName},
            ...contact.firstName && {firstName: contact.firstName},
            ...contact.parentalName && {parentalName: contact.parentalName},
            ...contact.address && {address: contact.address},
            ...contact.source && {source: contact.source},
            ...contact.type && {type: contact.type}
        });

        this.archived.setValue(!!contact.archived);

        if (contact.phones && contact.phones.length) {
            this.patchPhonesForm(contact.phones);
        }

        if (contact.emails && contact.emails.length) {
            this.patchEmailsForm(contact.emails);
        }
    }

    patchManager() {
        if (!this.contactsForm) {
            this.initForm();
        }

        this.contactsForm.get('manager')?.setValue({id: this.currentUser?.id, name: this.currentUser?.name});

        if (this.data.edit && this.checkPermissionAndOwner('PROJECT_EDIT', 'PROJECT_OWN_EDIT', this.data.contactToEdit)) {
            this.readonlyFlag = true;
        }
    }

    patchPhonesForm(phones: string[]) {
        this.initSetPhonesControls();
        const difference = phones.length - this.getPhonesControlsQuantity();
        if (difference > 0) {
            for (let i = 0; i < difference; i++) {
                this.addPhonesArrayItem();
            }
        }

        for (const i in phones) {
            const phone: string = phones[i];
            const controls = this.getPhonesControls();
            if (controls[i]) {
                controls[i].get('phone')!.setValue(phone.substr(3));
            }
        }
    }

    patchEmailsForm(emails: string[]) {
        this.initSetEmailsControls();
        const difference = emails.length - this.getEmailsControlsQuantity();
        if (difference > 0) {
            for (let i = 0; i < difference; i++) {
                this.addEmailsArrayItem();
            }
        }

        for (const i in emails) {
            const email: string = emails[i];
            const controls = this.getEmailsControls();
            if (controls[i]) {
                controls[i].get('email')!.setValue(email);
            }
        }
    }

    /** Add Or Edit Agreement */
    submitForm(closeDialog: boolean) {
        this.closeDialogFlag = closeDialog;
        const formData = this.contactsForm.getRawValue();
        const phonesFormData = this.phonesForm.getRawValue();
        const emailsFormData = this.emailsForm.getRawValue();
        const phones = phonesFormData?.phonesFormControls?.length
            ? phonesFormData.phonesFormControls
                .filter((item: any) => item?.phone)
                .map((item: any) => `+38${item.phone}`)
            : null;
        const emails = emailsFormData.emailsFormControls && emailsFormData.emailsFormControls.length ? emailsFormData.emailsFormControls.map((item: any) => item.email) : null;
        const request: ContactRequest = {
            ...formData.lastName && {lastName: formData.lastName},
            ...formData.firstName && {firstName: formData.firstName},
            ...formData.parentalName && {parentalName: formData.parentalName},
            ...formData.manager && {managerId: formData.manager.id},
            ...formData.source && {source: formData.source},
            ...formData.type && {type: formData.type},
            ...formData.address && {address: formData.address},
            ...phones && phones.length && {phones: phones},
            ...emails && emails.length && {emails: emails.filter((item: string) => item !== '')},
            archived: !!this.archived.value
        };

        if (this.data.edit && this.data.contactToEdit) {
            this.store$.dispatch(new contactsActions.EditContact({contactId: this.data.contactToEdit.id!, request}));
        } else {
            this.store$.dispatch(new contactsActions.AddContact(request));
        }
    }

    /** Watch Actions */
    watchAddOrEditContactSuccess() {
        this.actions$.pipe(
            ofType(
                contactsActions.ContactsActionTypes.AddContactSuccess,
                contactsActions.ContactsActionTypes.EditContactSuccess,
            ),
            takeUntil(this.destroyed$),
        ).subscribe((action: contactsActions.AddContactSuccess | contactsActions.EditContactSuccess) => {
            if (action.isSocket && action.type === '[Contacts] Add Contact Success') {
                return;
            }

            if (action.type === '[Contacts] Edit Contact Success' && action.payload.id !== this.data.contactToEdit?.id) {
                return;
            }

            if (action.type === '[Contacts] Add Contact Success') {
                if (this.closeDialogFlag) {
                    this.closeDialog(true);
                } else {
                    if (!this.data.edit) {
                        this.data.edit = true;
                        this.updateWidth();
                    }
                    this.data.contactToEdit = action.payload;
                }
            }

            if (this.closeDialogFlag) {
                this.closeDialog(true);
            }

            this.loading$.next(false);
        });
    }

    watchAddOrEditContactFail() {
        this.actions$.pipe(
            ofType(
                contactsActions.ContactsActionTypes.AddContactFailure,
                contactsActions.ContactsActionTypes.EditContactFailure,
            ),
            takeUntil(this.destroyed$),
        ).subscribe(() => {
            this.loading$.next(false);
        });
    }

    scrollToBottom() {
        this.scrollableContent.nativeElement.scrollTop = this.scrollableContent.nativeElement.scrollHeight;
    }

    /** Minimize Dialog */
    minimize() {
        SharedService.minimize(
            this.dialogRef.id,
            `${this.data.contactToEdit?.lastName} ${this.data.contactToEdit?.firstName}`,
            this.store$,
            this.data.contactToEdit?.id,
            false,
            '',
            `${SharedService.getTranslation(this.translateService, 'modules.crm.contacts.contactDialog.title')} - ${this.data.contactToEdit?.lastName} ${this.data.contactToEdit?.firstName}`,
        );
    }

    /** Close dialog */
    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
        this.store$.dispatch(new mainActions.CloseMinimizedDialog(this.dialogRef.id));
    }
}
